import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getStyles, createUniqueIdentifier, translate, addModifierClasses, addToArray, getEventAttributes } from '../../../shared/utility';

import Button from '../Button/Button';
import Dots from '../Loading/Dots/Dots';
import Dropdown from '../Dropdown/Dropdown';

import CharacterCounter from './CharacterCounter/CharacterCounter';

import localStyles from './Input.scss';
const styles = getStyles(localStyles);

class Input extends PureComponent {
    /* * * * * * * * * *
     * REACT LIFECYCLE *
     * * * * * * * * * */
    constructor(props) {
        super(props);
        this.state = {
            input_id: props.id || (props.attrs ? props.attrs.id : false) || createUniqueIdentifier('input', 10)
        };
    }

    /* * * * * *
     * RENDER  *
     * * * * * */
    render() {

        // get attributes form this.props.attrs
        const attrs = this.props.attrs || {};
        const isToggleInput = attrs.type === 'radio' || attrs.type === 'checkbox';

        // return <Button /> when the type is a button
        if (['button', 'reset', 'submit'].includes(attrs.type)) {
            return <Button {...this.props} />;
        }

        // set description / error
        let description = null;
        if (this.props.invalid && this.props.touched) {
            description = <p className={styles('tiny', 'c--error', 'description')}>{this.props.error || translate('ui.defaultInputError', this.props.language)}</p>;
        } else if (this.props.loading) {
            description = <p className={styles('tiny', 'c--text-light', 'description')}><Dots min={1} max={5} /></p>;
        } else if (this.props.description) {
            description = <p className={styles('tiny', 'c--text-light', 'description')}>{this.props.description}</p>;
        }

        let inputClasses = ['input'];
        const modifiers = this.props.modifiers ? this.props.modifiers.slice() : [];
        const classes = (this.props.classes || []).concat(attrs.classes || []);

        if (!isToggleInput) {
            if (description) {
                modifiers.push('collapsed');
            }
            if (this.props.invalid && this.props.touched) {
                modifiers.push('invalid');
            }
            if (attrs.type === 'textarea') {
                modifiers.push('textarea');
            }

            inputClasses = addModifierClasses(modifiers, 'input', inputClasses);
            inputClasses = addToArray(classes, inputClasses);
        }

        // set html attributes for inputElement
        let properties = {
            id: this.state.input_id,
            name: this.props.name || attrs.name,

            className: styles(isToggleInput ? 'input' : inputClasses),

            value: this.props.value,

            readOnly: this.props.readonly || this.props.loading ? true : false,
            disabled: this.props.disabled ? true : false,
            required: this.props.required ? true : false,
            checked: this.props.checked ? true : false // no, really, fuck you react
        };

        // add events from this.props.events to properties
        properties = getEventAttributes(this.props.events, properties);

        // extend attributes set by this.props.attrs with properties
        const htmlAttributes = Object.assign({}, attrs, properties);

        let inputElement = null;

        switch (attrs.type) {
            case 'textarea':
                inputElement = <textarea {...htmlAttributes}>{htmlAttributes.value}</textarea>;
                break;
            case 'select':
                inputElement = (
                    <Dropdown
                        label={this.props.label || this.props.children}
                        labelClasses={this.props.labelClasses}
                        labelContentClasses={this.props.labelContentClasses}
                        isSelect={true}
                        options={this.props.options}
                        callback={htmlAttributes.onChange}
                        required={htmlAttributes.required}
                    />
                );
                break;
            default:
                htmlAttributes.type = attrs.type || 'text';
                if (htmlAttributes.type === 'number') {
                    htmlAttributes.inputMode = 'numeric';
                }
                inputElement = <input {...htmlAttributes} />;
                break;
        }

        // create <label> element
        const labelContent = this.props.label || this.props.children;

        let label = null;

        if (attrs.type !== 'select' && (labelContent || isToggleInput)) {
            const labelModifiers = []
                .concat(htmlAttributes.disabled ? 'disabled' : null)
                .concat(isToggleInput ? modifiers : null)
                .concat(isToggleInput && !modifiers.includes('button') ? 'toggle' : null);

            let labelClasses = ['label']
                .concat(this.props.addCounter ? ['flex', 'flex--justify-between', 'flex--align-baseline'] : []);
            labelClasses = addModifierClasses(labelModifiers, 'label', labelClasses);
            labelClasses = addToArray(this.props.labelClasses, labelClasses);

            const labelContentModifiers = []
                .concat(htmlAttributes.required ? 'required' : null)
                .concat(isToggleInput ? modifiers : null)
                .concat(isToggleInput && !modifiers.includes('button') ? 'toggle' : null);

            let labelContentClasses = ['label__content'];
            labelContentClasses = addModifierClasses(labelContentModifiers, 'label__content', labelContentClasses);
            labelContentClasses = addToArray(this.props.labelContentClasses, labelContentClasses);

            label = <label
                className={styles(labelClasses)}
                htmlFor={htmlAttributes.id || null}
            >
                {isToggleInput ? inputElement : null}

                <span className={styles(labelContentClasses)}>
                    {labelContent}
                </span>

                {this.props.addCounter && (attrs.type === 'textarea' || htmlAttributes.type === 'text') ?
                    <CharacterCounter
                        current={this.props.value.length}
                        min={this.props.shouldValidate ? this.props.shouldValidate.minLength ? this.props.shouldValidate.minLength : this.props.shouldValidate.required ? 1 : -1 : -1}
                        max={this.props.shouldValidate ? this.props.shouldValidate.maxLength : -1}
                    /> : null
                }
            </label>;
        }

        const wrapperClasses = inputClasses
            .filter(className => className.substr(0, 13) === 'input--inline')
            .map(className => className.replace('input--inline', 'input-wrapper'));

        return wrapperClasses.length ? (
            <div className={styles(wrapperClasses)}>
                {label}
                {isToggleInput ? null : inputElement}
                {isToggleInput ? null : description}
            </div>
        ) : (
            <Fragment>
                {label}
                {isToggleInput ? null : inputElement}
                {isToggleInput ? null : description}
            </Fragment>
        );
    }
}

Input.propTypes = {
    // properties
    children: PropTypes.node,

    attrs: PropTypes.object,
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    readonly: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    checked: PropTypes.bool,
    invalid: PropTypes.bool,
    loading: PropTypes.bool,
    touched: PropTypes.bool,
    error: PropTypes.string,
    events: PropTypes.object,
    shouldValidate: PropTypes.object,
    classes: PropTypes.string,
    labelClasses: PropTypes.string,
    labelContentClasses: PropTypes.string,
    label: PropTypes.string,
    modifiers: PropTypes.arrayOf(PropTypes.string),
    addCounter: PropTypes.bool,
    description: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.any.isRequired,
            text: PropTypes.string,
            selected: PropTypes.bool,
            html: PropTypes.node
        })
    ),

    language: PropTypes.string.isRequired
};

const mapStateToProps = state => {
    return {
        language: state.global.localization.language
    };
};

export default connect(mapStateToProps, null)(Input);



// WEBPACK FOOTER //
// ./src/components/UI/Input/Input.js